import { formatCategoryDisplay } from "@/lib/utils";
import { isDerivedParentTagId } from "../../hooks/useMultiGroupTagFilter";
import { VERIFIED_FILTER_ID } from "../../utils/parseTagFilters";
import type { Tag, TagGroup } from "./types";

export type FilterSelectionState = "checked" | "indeterminate" | "unchecked";

export function withoutLockedProjectFilters(filters: string[], isProjectLocked: boolean) {
  return isProjectLocked ? filters.filter((value) => !value.startsWith("project-")) : filters;
}

function derivedChildIdsByParent(tagGroups: TagGroup[]): Map<string, string[]> {
  const entries = tagGroups.flatMap((group) =>
    group.tags.flatMap((tag) =>
      // `as const` keeps the pair a tuple: without it TS widens it to
      // `(string | string[])[]` and the Map constructor has no matching overload.
      isDerivedParentTagId(tag.id) && tag.childTagIds?.length
        ? [[tag.id, tag.childTagIds] as const]
        : []
    )
  );
  return new Map(entries);
}

/** Replace legacy derived-parent IDs with the child IDs the panel stores. */
export function normalizeDerivedParentIds(filters: string[], tagGroups: TagGroup[]): string[] {
  const childIdsByParent = derivedChildIdsByParent(tagGroups);
  const expanded = filters.flatMap((id) => childIdsByParent.get(id) ?? [id]);
  return Array.from(new Set(expanded));
}

export function getTagSelectionState(tag: Tag, selectedTags: string[]): FilterSelectionState {
  if (selectedTags.includes(tag.id)) return "checked";
  if (!tag.childTagIds?.length) return "unchecked";

  const selectedChildren = tag.childTagIds.filter((id) => selectedTags.includes(id)).length;
  if (selectedChildren === 0) return "unchecked";
  // Only a derived parent may read as fully checked off its children's backs —
  // it is what `toggleFilter` clears in one click. A parent that owns a filter
  // ID stays indeterminate until that ID itself is selected, so its row can
  // always be toggled (and it is honest: selecting every child does not match
  // documents tagged with the parent category alone).
  return isDerivedParentTagId(tag.id) && selectedChildren === tag.childTagIds.length
    ? "checked"
    : "indeterminate";
}

export function getTagDisplayLabel(group: TagGroup, tag: Tag) {
  return group.id === "categories" ? formatCategoryDisplay(tag.label) : tag.label;
}

/**
 * Count filters as the panel presents them, not as IDs. `toggleFilter` expands
 * a derived parent (`filter-group-*`) into every one of its children and never
 * stores the parent ID, so one click on one row writes several IDs — collapse a
 * fully selected parent back into the single row the user actually picked.
 *
 * Takes filter IDs that have been through `normalizeDerivedParentIds`, so a
 * derived parent is only ever present as its children.
 */
export function countSelectedRows(filterIds: string[], tagGroups: TagGroup[]): number {
  // The badge describes rows the open panel can actually show as selected.
  // Project switches can briefly leave old IDs in parent state while the new
  // options load; counting those would advertise selections with no matching
  // checkbox. Verified is rendered outside the grouped rows but is still a
  // visible option.
  const renderedIds = new Set<string>([VERIFIED_FILTER_ID]);
  tagGroups.forEach((group) =>
    group.tags.forEach((tag) => {
      renderedIds.add(tag.id);
      tag.childTagIds?.forEach((childId) => renderedIds.add(childId));
    })
  );
  const selected = new Set(filterIds.filter((id) => renderedIds.has(id)));
  const collapsed = new Set<string>();
  let parentRows = 0;

  for (const group of tagGroups) {
    for (const tag of group.tags) {
      if (!isDerivedParentTagId(tag.id) || !tag.childTagIds?.length) continue;
      if (!tag.childTagIds.every((id) => selected.has(id))) continue;
      parentRows += 1;
      tag.childTagIds.forEach((childId) => collapsed.add(childId));
    }
  }

  // Counted over the set, not the array: a duplicate ID is still one row.
  return parentRows + [...selected].filter((id) => !collapsed.has(id)).length;
}
